import { EntityHandler, EntityListResult, SectionHandlerGroup, FormSectionHandler } from '../entity.handler';
import { IKuiTableCol } from 'app/key-ui';
import { Injectable } from '@angular/core';
import { ThemeResponse, ThemeCreateRequest, ThemeUpdateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { EnumToComboValues, GridColumnHelper } from '../utils';
import { ThemeDetailsSectionHandler } from './details.section';
import { ICONS } from '../icons';
import { RegisterEntityHandler } from '../../entities';
import { getThemeDictionary, isLegacyTheme } from 'app/shared/components/theme/theme-dictionary';
import { MainSettingsSectionHandler } from './settings/main.section';
import { ContentSettingsSectionHandler } from './settings/content.section';
import { ColorSettingsSectionHandler } from './settings/color.section';
import { HeaderSettingsSectionHandler } from './settings/header.section';
import { FooterSettingsSectionHandler } from './settings/footer.section';
import { NavigationSettingsSectionHandler } from './settings/navigation.section';
import { AccordionSettingsSectionHandler } from './settings/accordion.section';
import { TableSettingsSectionHandler } from './settings/table.section';
import { AppService } from 'app/app.service';
import { TreeSettingsSectionHandler } from './settings/tree.section';
import { ListSettingsSectionHandler } from './settings/list.section';
import { FeedSettingsSectionHandler } from './settings/feed.section';
import { TabSettingsSectionHandler } from './settings/tab.section';
import { InputSettingsSectionHandler } from './settings/input.section';
import { ThemeEngine } from 'app/shared/components/theme';
import { LegacyThemeSettingsSectionHandler } from './legacy-settings.section';
import { GraphSettingsSectionHandler } from './settings/graph.section';
import { MapSettingsSectionHandler } from './settings/map.section';
import { ButtonSettingsSectionHandler } from './settings/button.section';
import { TopNavigationSettingsSectionHandler } from './settings/topnavigation.section';
import { ModalSettingsSectionHandler } from './settings/modal.section';
import { SubHeaderSettingsSectionHandler } from './settings/subheader.section';
import { SidebarSettingsSectionHandler } from './settings/sidebar.section';
import { LoaderSettingsSectionHandler } from './settings/loader.section';
import { ThemeIconsSectionHandler } from './icons.section';
import { IconsLocationSettingsSectionHandler } from './settings/icons-location.section';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';

@RegisterEntityHandler('theme')
@Injectable()
export class ThemeHandler extends EntityHandler<ThemeResponse> {

    themeDictionary = getThemeDictionary();

    themeEngine: ThemeEngine;

    constructor(
        private app: AppService,
        admin: AdminEditorService,
        private httpClient: HttpClient,
        private sanitizer: DomSanitizer
    ) {
        super(admin);
    }

    states = ['active', 'deleted'];

    listEntities(ownerId: string, offset: number, limit: number, sort: string, filter: string): Promise<EntityListResult> {
        return this.admin.getCached(`listThemes/${ownerId}/false/${offset}/${limit}/${sort}/${filter}`, api => api.entities.listThemes(ownerId, false, offset, limit, sort, filter), 3).then(response => ({
            ...response,
            type: 'theme',
            title: this.admin.i18n.instant('ADMIN.ENTITIES.THEMES'),
        }));
    }

    getEntity(id: string): Promise<ThemeResponse> {
        return this.admin.getCached(`getTheme/${id}`, api => api.entities.getTheme(id), 10, true).then(entity => {
            this.themeEngine = new ThemeEngine();
            this.themeEngine.settingsToVariables(entity.settings);
            return entity;
        });
    }

    createEntity(ownerId: string, entity: ThemeCreateRequest): Promise<ThemeResponse> {
        entity.ownerId = ownerId;
        return this.admin.applyUpdate(api => api.entities.createTheme(entity));
    }

    setEntityState(entity: any, state: string): Promise<any> {
        return this.admin.applyUpdate(api => api.entities.updateTheme(entity.id, { state: state as any }));
    }

    async getGridColumns(_ownerId: string): Promise<IKuiTableCol[]> {
        const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.THEME.COLUMNS');
        const baseThemes = Object.entries(this.themeDictionary).map(key => ({ id: key[0], value: key[1].name }));
        const columns: IKuiTableCol[] = [
            grid.IconTextColumn('name', 'NAME', ICONS.theme),
            grid.TextColumn('description', 'DESCRIPTION'),
            grid.ComboColumn('theme', 'BASE_THEME', baseThemes, 100),
            grid.ComboColumn('state', 'STATE', EnumToComboValues(this.admin.i18n, 'ADMIN.STATES', this.states, ['deleted']), 80),
        ];
        columns.push(grid.TimeAgoColumn('modifiedDate', 'MODIFIED'));
        return columns;
    }

    async getEditorSections(ownerId: string, entity: ThemeResponse): Promise<SectionHandlerGroup<ThemeResponse>[]> {

        this.app.setTheme(entity);

        if (isLegacyTheme(entity)) {
            return [{
                name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
                sections: [
                    new ThemeDetailsSectionHandler(this.admin, ownerId, false, true, this.themeEngine, this.httpClient),
                    new LegacyThemeSettingsSectionHandler(this.admin, ownerId),
                ],
            }];
        } else {
            return [{
                name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
                sections: [
                    new ThemeDetailsSectionHandler(this.admin, ownerId, false, false, this.themeEngine, this.httpClient),
                    new MainSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new ThemeIconsSectionHandler(this.admin, ownerId, this.httpClient, this.sanitizer),
                    new ColorSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new ContentSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new NavigationSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new TopNavigationSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new HeaderSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new SubHeaderSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new FooterSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new ModalSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new SidebarSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                ],
            }, {
                name: this.admin.i18n.instant('ADMIN.EDITOR.CONTROLS'),
                sections: [
                    new ButtonSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new InputSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new TabSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new AccordionSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new TableSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new TreeSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new ListSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new IconsLocationSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new FeedSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new GraphSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new MapSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                    new LoaderSettingsSectionHandler(this.admin, ownerId, this.themeEngine),
                ],
            }];
        }
    }

    async getCreationSections(ownerId: string): Promise<{ entityType: string, entity: ThemeUpdateRequest, sections: FormSectionHandler<ThemeResponse, any>[] }> {
        return {
            entityType: 'theme',
            entity: {
                theme: 'default',
            }, // set up some defaults if you want
            sections: [
                new ThemeDetailsSectionHandler(this.admin, ownerId, true, false, this.themeEngine, this.httpClient),
            ],
        };
    }

}













